/* eslint-disable react-hooks/exhaustive-deps */
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    updateDoc,
    where,
} from "firebase/firestore";
import { auth, db } from "../../../firebase";
import { ObjectifStudent } from "./objectifStudent.type";
import { useLocationState } from "../../Utils";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Objectif } from "../objectif/objectif.type";



export const ObjectifStudentApi = createApi({
    reducerPath: "objectifStudentApi",
    tagTypes: ["objectifStudent", "objectifStudents", 'objectifsByStudent'],
    baseQuery: fakeBaseQuery(),
    endpoints: (builder) => ({
        addOrEditObjectifStudent: builder.mutation({
            async queryFn({ id, ...data }) {
                try {
                    const user = auth.currentUser;
                    if (!user) {
                        return { error: { message: "Aucun utilisateur connecté." } };
                    }
                    const userId = user?.uid;
                    if (!id) {
                        const docRef = await addDoc(collection(db, "objectifStudents"), { ...data, createdAt: serverTimestamp(), createdBy: userId, updatedAt: serverTimestamp() });
                        return {
                            data: {
                                id: docRef.id,
                                message: "Objectif créé !",
                                ...data,
                            },
                        };

                    } else {
                        // Mettre à jour Firestore
                        const allDone = data.sousObjectifs?.every((s: any) => s.done === true);

                        const docRef = doc(db, "objectifStudents", id);
                        await updateDoc(docRef, {
                            ...data,
                            ...(allDone ? { status: "Terminé" } : { status: "En cours" }),
                            updatedAt: serverTimestamp(),
                        });

                        return {
                            data: {
                                message:
                                    "Objectif modifié !",
                            },
                        };
                    }
                } catch (error: any) {
                    return { error: { status: "CUSTOM", message: error.message } };
                }
            },
            invalidatesTags: ["objectifStudent", "objectifStudents"],
        }),
        getObjectifStudents: builder.query({
            async queryFn() {
                try {
                    return new Promise((resolve) => {
                        const q = query(
                            collection(db, "objectifStudents"),
                            orderBy("createdAt", "desc"),
                        );
                        onSnapshot(q, (snapshot) => {
                            const ObjectifStudents = snapshot.docs.map((doc) => ({
                                id: doc.id,
                                ...doc.data(),
                            }));
                            resolve({ data: ObjectifStudents as ObjectifStudent[] });
                        });
                        // unsubscribe();
                    });
                } catch (error) {
                    return { error };
                }
            },
            keepUnusedDataFor: 0,
            async onCacheEntryAdded(
                arg,
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                const ObjectifStudentsRef = query(
                    collection(db, "objectifStudents"),
                    orderBy("createdAt", "desc"),
                );
                let unsubscribe;
                try {
                    await cacheDataLoaded;

                    unsubscribe = onSnapshot(ObjectifStudentsRef, (snapshot) => {
                        const ObjectifStudents = snapshot.docs.map((doc) => ({
                            id: doc.id,
                            ...doc.data(),
                        }));
                        updateCachedData(() => ObjectifStudents as ObjectifStudent[]);
                    });
                } catch (error) {
                    console.error("Erreur de souscription:", error);
                }

                // Nettoyer l'écouteur Firestore quand le cache est supprimé
                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe();
            },
            providesTags: ["objectifStudents"],
        }),
        getObjectifStudentByProf: builder.query({
            async queryFn(studentId: string) {
                try {
                    const user = auth.currentUser;
                    if (!user) {
                        return { error: { message: "Aucun utilisateur connecté." } };
                    }

                    const userId = user.uid;

                    const q = query(
                        collection(db, "objectifStudents"),
                        where("createdBy", "==", userId),
                        where('studentId', "==", studentId),
                        orderBy("createdAt", "desc")
                    );

                    return new Promise((resolve) => {
                        onSnapshot(q, async (snapshot) => {

                            const objectifStudents = await Promise.all(
                                snapshot.docs.map(async (objectifStudent) => {
                                    const data = objectifStudent.data();

                                    // Récupérer objectif
                                    let objectifData = null;
                                    if (data.objectifId) {
                                        const objectifSnap = await getDoc(
                                            doc(db, "objectifs", data.objectifId)
                                        );
                                        if (objectifSnap.exists()) {
                                            objectifData = {
                                                id: objectifSnap.id,
                                                ...objectifSnap.data(),
                                            };
                                        }
                                    }

                                    // Récupérer matiere
                                    let matiereData = null;
                                    if (data.matiereId) {
                                        const matiereSnap = await getDoc(
                                            doc(db, "matieres", data.matiereId)
                                        );
                                        if (matiereSnap.exists()) {
                                            matiereData = {
                                                id: matiereSnap.id,
                                                ...matiereSnap.data(),
                                            };
                                        }
                                    }

                                    // Récupérer student
                                    let studentData = null;
                                    if (data.studentId) {
                                        const studentSnap = await getDoc(
                                            doc(db, "users", data.studentId)
                                        );
                                        if (studentSnap.exists()) {
                                            studentData = {
                                                id: studentSnap.id,
                                                ...studentSnap.data(),
                                            };
                                        }
                                    }

                                    return {
                                        id: objectifStudent.id,
                                        ...data,
                                        objectifData,
                                        studentData,
                                    };
                                })
                            );

                            resolve({ data: objectifStudents });
                        });
                    });

                } catch (error) {
                    return { error };
                }
            },

            keepUnusedDataFor: 0,

            async onCacheEntryAdded(
                arg,
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                const ObjectifStudentsRef = query(
                    collection(db, "objectifStudents"),
                    where("createdBy", "==", auth.currentUser?.uid),
                    where("studentId", "==", arg),
                    orderBy("createdAt", "desc")
                );

                let unsubscribe;

                try {
                    await cacheDataLoaded;

                    unsubscribe = onSnapshot(ObjectifStudentsRef, async (snapshot) => {

                        const objectifStudents = await Promise.all(
                            snapshot.docs.map(async (objectifStudent) => {
                                const data = objectifStudent.data();

                                // Récupérer objectif
                                let objectifData = null;
                                if (data.objectifId) {
                                    const objectifSnap = await getDoc(
                                        doc(db, "objectifs", data.objectifId)
                                    );
                                    if (objectifSnap.exists()) {
                                        objectifData = {
                                            id: objectifSnap.id,
                                            ...objectifSnap.data(),
                                        };
                                    }
                                }

                                // Récupérer matiere
                                let matiereData = null;
                                if (data.matiereId) {
                                    const matiereSnap = await getDoc(
                                        doc(db, "matieres", data.matiereId)
                                    );
                                    if (matiereSnap.exists()) {
                                        matiereData = {
                                            id: matiereSnap.id,
                                            ...matiereSnap.data(),
                                        };
                                    }
                                }

                                // Récupérer student
                                let studentData = null;
                                if (data.studentId) {
                                    const studentSnap = await getDoc(
                                        doc(db, "users", data.studentId)
                                    );
                                    if (studentSnap.exists()) {
                                        studentData = {
                                            id: studentSnap.id,
                                            ...studentSnap.data(),
                                        };
                                    }
                                }

                                return {
                                    id: objectifStudent.id,
                                    ...data,
                                    objectifData,
                                    studentData,
                                    matiereData,
                                };
                            })
                        );

                        updateCachedData(() => objectifStudents);
                    });

                } catch (error) {
                    console.error("Erreur de souscription:", error);
                }

                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe();
            },

            providesTags: ["objectifStudents"],
        }),
        getObjectifStudentById: builder.query({
            queryFn: async (ObjectifStudentId) => {
                try {
                    const ObjectifStudentRef = doc(db, "objectifStudents", ObjectifStudentId);
                    const ObjectifStudentSnap = await getDoc(ObjectifStudentRef);

                    if (!ObjectifStudentSnap.exists()) {
                        return {
                            error: { status: 404, message: "ObjectifStudent introuvable !" },
                        };
                    }

                    return { data: { id: ObjectifStudentSnap.id, ...ObjectifStudentSnap.data() } };
                } catch (error: any) {
                    return { error: { status: 500, message: error.message } };
                }
            },
            providesTags: ["objectifStudent"],
        }),
        // Supprimer
        deleteObjectifStudent: builder.mutation({
            queryFn: async (uid) => {
                try {
                    const docRef = doc(db, "objectifStudents", uid);

                    // 1. Récupérer le document avant suppression
                    const docSnap = await getDoc(docRef);
                    const objectifStudent = docSnap.data() as ObjectifStudent;
                    const { objectifId, studentId } = objectifStudent;

                    // 2. Supprimer l'objectifStudent
                    await deleteDoc(docRef);

                    // 3. Mettre à jour l'objectif lié
                    if (objectifId) {
                        const objectifRef = doc(db, "objectifs", objectifId);
                        const objectifSnap = await getDoc(objectifRef);
                        const objectifData = objectifSnap.data() as Objectif;

                        // Retirer le student de la liste
                        const updatedStudents = objectifData.students.filter(
                            (id) => id !== studentId
                        );

                        await updateDoc(objectifRef, {
                            students: updatedStudents,
                            updatedAt: serverTimestamp(),
                        });
                    }

                    return {
                        data: { uid, message: "Objectif supprimé !", success: true },
                    };
                } catch (error: any) {
                    return { error: { status: "DELETE_ERROR", message: error.message } };
                }
            },
            invalidatesTags: ["objectifStudent", "objectifStudents"],
        }),
        getObjectifsByStudent: builder.query({
            async queryFn(id?: string) {
                try {
                    if (!id) {
                        return { data: [] as ObjectifStudent[] };
                    }

                    const studentId = id;

                    const q = query(
                        collection(db, "objectifStudents"),
                        where('studentId', "==", studentId),
                        orderBy("createdAt", "desc")
                    );

                    return new Promise((resolve) => {
                        onSnapshot(q, async (snapshot) => {

                            const objectifStudents = await Promise.all(
                                snapshot.docs.map(async (objectifStudent) => {
                                    const data = objectifStudent.data();

                                    // Récupérer objectif
                                    let objectifData = null;
                                    if (data.objectifId) {
                                        const objectifSnap = await getDoc(
                                            doc(db, "objectifs", data.objectifId)
                                        );
                                        if (objectifSnap.exists()) {
                                            objectifData = {
                                                id: objectifSnap.id,
                                                ...objectifSnap.data(),
                                            };
                                        }
                                    }

                                    // Récupérer matiere
                                    let matiereData = null;
                                    if (data.matiereId) {
                                        const matiereSnap = await getDoc(
                                            doc(db, "matieres", data.matiereId)
                                        );
                                        if (matiereSnap.exists()) {
                                            matiereData = {
                                                id: matiereSnap.id,
                                                ...matiereSnap.data(),
                                            };
                                        }
                                    }

                                    // Récupérer student
                                    let studentData = null;
                                    if (data.studentId) {
                                        const studentSnap = await getDoc(
                                            doc(db, "users", data.studentId)
                                        );
                                        if (studentSnap.exists()) {
                                            studentData = {
                                                id: studentSnap.id,
                                                ...studentSnap.data(),
                                            };
                                        }
                                    }

                                    return {
                                        id: objectifStudent.id,
                                        ...data,
                                        objectifData,
                                        studentData,
                                    };
                                })
                            );

                            resolve({ data: objectifStudents });
                        });
                    });

                } catch (error) {
                    return { error };
                }
            },

            keepUnusedDataFor: 0,

            async onCacheEntryAdded(
                arg,
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                if (!arg) {
                    return;
                }

                const ObjectifStudentsRef = query(
                    collection(db, "objectifStudents"),
                    where("studentId", "==", arg),
                    orderBy("createdAt", "desc")
                );

                let unsubscribe;

                try {
                    await cacheDataLoaded;

                    unsubscribe = onSnapshot(ObjectifStudentsRef, async (snapshot) => {

                        const objectifStudents = await Promise.all(
                            snapshot.docs.map(async (objectifStudent) => {
                                const data = objectifStudent.data();

                                // Récupérer objectif
                                let objectifData = null;
                                if (data.objectifId) {
                                    const objectifSnap = await getDoc(
                                        doc(db, "objectifs", data.objectifId)
                                    );
                                    if (objectifSnap.exists()) {
                                        objectifData = {
                                            id: objectifSnap.id,
                                            ...objectifSnap.data(),
                                        };
                                    }
                                }

                                // Récupérer matiere
                                let matiereData = null;
                                if (data.matiereId) {
                                    const matiereSnap = await getDoc(
                                        doc(db, "matieres", data.matiereId)
                                    );
                                    if (matiereSnap.exists()) {
                                        matiereData = {
                                            id: matiereSnap.id,
                                            ...matiereSnap.data(),
                                        };
                                    }
                                }

                                // Récupérer student
                                let studentData = null;
                                if (data.studentId) {
                                    const studentSnap = await getDoc(
                                        doc(db, "users", data.studentId)
                                    );
                                    if (studentSnap.exists()) {
                                        studentData = {
                                            id: studentSnap.id,
                                            ...studentSnap.data(),
                                        };
                                    }
                                }

                                return {
                                    id: objectifStudent.id,
                                    ...data,
                                    objectifData,
                                    studentData,
                                    matiereData,
                                };
                            })
                        );

                        updateCachedData(() => objectifStudents);
                    });

                } catch (error) {
                    console.error("Erreur de souscription:", error);
                }

                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe();
            },

            providesTags: ["objectifsByStudent"],
        }),
    }),
});

export const {
    useAddOrEditObjectifStudentMutation,
    useGetObjectifStudentsQuery,
    useGetObjectifStudentByProfQuery,
    useDeleteObjectifStudentMutation,
    useLazyGetObjectifStudentByIdQuery,
    useGetObjectifsByStudentQuery,
} = ObjectifStudentApi;

export function useObjectifStudentFromLocation(): [
    ObjectifStudent,
    boolean,
    string,
    (id: string) => any
] {
    const itemState = useLocationState<ObjectifStudent | undefined>(undefined);
    const [item, setItem] = useState(itemState);
    const { id } = useParams<{ id: string }>();
    const [findById, { data, isError, isLoading }] = useLazyGetObjectifStudentByIdQuery();
    const navigate = useNavigate();

    useEffect(() => {
        // console.log("is id", id)
        if (id && id !== undefined) {
            // console.log("id", id)
            findById(id as string);
        }
    }, [id]);
    useEffect(() => {
        if (isError && !itemState) {
            navigate(-1);
        }
    }, [isError]);
    useEffect(() => {
        if (data) {
            setItem(data as ObjectifStudent);
        }
    }, [data]);

    return [item as ObjectifStudent & { bio: string }, isLoading, id as string, findById];
}